/**
 * ListaPrecio Form Dialog - Create/Edit price lists
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreateListaPrecio,
  useUpdateListaPrecio,
  type ListaPrecio,
} from "@/hooks/use-facturacion";
import { TIPOS_LISTA_PRECIO } from "@/hooks/use-facturacion";

interface ListaPrecioFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listaPrecio: ListaPrecio | null;
  onSuccess: (isNew: boolean) => void;
}

const schema = z.object({
  nombre: z.string().min(1, "facturacion.listasPrecios.validation.nombreRequired"),
  tipo: z.string().min(1, "facturacion.listasPrecios.validation.tipoRequired"),
  descripcion: z.string().optional(),
  porcentaje_ajuste: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

export function ListaPrecioFormDialog({
  open,
  onOpenChange,
  listaPrecio,
  onSuccess,
}: ListaPrecioFormDialogProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const [error, setError] = useState<string | null>(null);

  const isEditing = !!listaPrecio;

  const createMutation = useCreateListaPrecio({
    onSuccess: () => onSuccess(true),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const updateMutation = useUpdateListaPrecio({
    onSuccess: () => onSuccess(false),
    onError: (err) => setError(getErrorMessage(err)),
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { nombre: "", tipo: "", descripcion: "", porcentaje_ajuste: "0" },
  });

  useEffect(() => {
    if (open && listaPrecio) {
      reset({
        nombre: listaPrecio.nombre,
        tipo: listaPrecio.tipo,
        descripcion: listaPrecio.descripcion || "",
        porcentaje_ajuste: String(listaPrecio.porcentaje_ajuste ?? "0"),
      });
      setError(null);
    } else if (open && !listaPrecio) {
      reset({ nombre: "", tipo: "", descripcion: "", porcentaje_ajuste: "0" });
      setError(null);
    }
  }, [open, listaPrecio, reset]);

  const onSubmit = (data: FormData) => {
    setError(null);
    const payload = {
      nombre: data.nombre,
      tipo: data.tipo as ListaPrecio["tipo"],
      descripcion: data.descripcion || "",
      porcentaje_ajuste: parseFloat(data.porcentaje_ajuste || "0"),
    };

    if (isEditing && listaPrecio) {
      updateMutation.mutate({ id: listaPrecio.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t("facturacion.listasPrecios.editTitle")
              : t("facturacion.listasPrecios.newTitle")}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t("facturacion.listasPrecios.editDesc")
              : t("facturacion.listasPrecios.newDesc")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">{t("facturacion.listasPrecios.fields.nombre")} *</Label>
              <Input id="nombre" {...register("nombre")} />
              {errors.nombre && (
                <p className="text-sm text-destructive">{t(errors.nombre.message as string)}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="tipo">{t("facturacion.listasPrecios.fields.tipo")} *</Label>
              <Select
                value={watch("tipo") || ""}
                onValueChange={(value) => setValue("tipo", value, { shouldValidate: true })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("facturacion.listasPrecios.fields.selectTipo")} />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_LISTA_PRECIO.map((tp) => (
                    <SelectItem key={tp.value} value={tp.value}>
                      {t(`facturacion.listasPrecios.tipos.${tp.value}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.tipo && (
                <p className="text-sm text-destructive">{t(errors.tipo.message as string)}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">
                {t("facturacion.listasPrecios.fields.descripcion")}
              </Label>
              <Textarea id="descripcion" rows={3} {...register("descripcion")} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="porcentaje_ajuste">
                {t("facturacion.listasPrecios.fields.porcentajeAjuste")}
              </Label>
              <div className="relative">
                <Input
                  id="porcentaje_ajuste"
                  type="number"
                  step="0.01"
                  min="-100"
                  max="999.99"
                  placeholder="0"
                  {...register("porcentaje_ajuste")}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  %
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                {t("facturacion.listasPrecios.fields.porcentajeAjusteHelp")}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
